/**
 * @file useFooterMenuEditor.ts
 * @description Действия «Меню → Подвал» колонками (phase19.16, приёмка Р2а;
 * SB113; uiux-footer-columns §4.6, §5): добавить колонку (сразу, без окна,
 * с фокусом в заголовке), колонку категорий каталога, переименовать на
 * месте, «Сколько показывать», удалить колонку (с пунктами — с вопросом),
 * удалить или скрыть пункт, перестановки.
 *
 * Все правки идут через `useSiteMenus` (версия части, 409, перечитывание) —
 * своего состояния меню здесь нет. Новую колонку узнаём по id, которого не
 * было до создания: после ответа сервер отдаёт свежее меню.
 */

import { useEffect, useRef, useState } from "react";

import type { SiteMenusApi } from "./useSiteMenus";
import type { ConfirmRequest } from "../../components/ui/use-confirm";
import type { FooterMenuHandlers } from "../../components/site-view/menu/footer-menu-actions";
import { emptyMenuForm, menuFieldsFromForm } from "../../lib/site-view/menu-form";
import type { SiteMenuItem } from "../../lib/site-view/types";

export interface FooterMenuEditorApi {
  handlers: FooterMenuHandlers;
  addColumn: (kind: "GROUP" | "CATALOG_CATEGORIES") => Promise<void>;
  rename: (column: SiteMenuItem, label: string | null) => Promise<boolean>;
  setLimit: (column: SiteMenuItem, limit: number | null) => void;
  busy: boolean;
}

interface Options {
  api: SiteMenusApi;
  tree: SiteMenuItem[];
  confirm: (request: ConfirmRequest) => Promise<boolean>;
  onEdit: (item: SiteMenuItem) => void;
}

const itemName = (item: SiteMenuItem): string => item.label ?? item.target.title;

export function useFooterMenuEditor({ api, tree, confirm, onEdit }: Options): FooterMenuEditorApi {
  const [busy, setBusy] = useState(false);
  const before = useRef<Set<string> | null>(null);

  // Новая колонка появилась в меню — фокус в её заголовок.
  useEffect(() => {
    const known = before.current;
    if (!known) return;
    const fresh = tree.find((n) => !known.has(n.id));
    if (!fresh) return;
    before.current = null;
    const input = document.getElementById(`footer-column-title-${fresh.id}`);
    input?.scrollIntoView({ block: "center" });
    input?.focus();
  }, [tree]);

  async function addColumn(kind: "GROUP" | "CATALOG_CATEGORIES") {
    const form = kind === "GROUP" ? emptyMenuForm("GROUP") : { ...emptyMenuForm("CATALOG_CATEGORIES"), label: "Каталог" };
    before.current = new Set(tree.map((n) => n.id));
    setBusy(true);
    const ok = await api.create("FOOTER", null, menuFieldsFromForm(form));
    setBusy(false);
    if (!ok) before.current = null;
  }

  async function remove(item: SiteMenuItem) {
    const ok = await confirm({ title: `Удалить пункт «${itemName(item)}»?`, description: "Сайт не изменится до публикации.", action: "Удалить", danger: true });
    if (ok) await api.remove(item.id);
  }

  async function removeColumn(column: SiteMenuItem) {
    const kids = column.children.length;
    // Пустую колонку удаляем сразу — терять нечего; с пунктами или автосписок — с вопросом.
    if (kids > 0 || column.linkType === "CATALOG_CATEGORIES") {
      const ok = await confirm({
        title: column.label ? `Удалить колонку «${column.label}»?` : "Удалить колонку без заголовка?",
        description: `${kids > 0 ? `Вместе с ней удалятся пункты: ${kids}. ` : ""}Сайт не изменится до публикации.`,
        action: "Удалить",
        danger: true,
      });
      if (!ok) return;
    }
    await api.remove(column.id);
  }

  const handlers: FooterMenuHandlers = {
    edit: onEdit,
    reorder: (next) => void api.reorder("FOOTER", next),
    toggleHidden: (item) => void api.update(item.id, { isHidden: !item.isHidden }),
    remove: (item) => void remove(item),
    removeColumn: (column) => void removeColumn(column),
  };

  return {
    handlers,
    addColumn,
    rename: (column, label) => api.update(column.id, { label }),
    setLimit: (column, autoLimit) => void api.update(column.id, { autoLimit }),
    busy,
  };
}
